import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Notification from '@/components/ui/Notification';
import showToast from '@/components/ui/Toast';
import communityService, { MabarParticipant } from '@/services/communityService';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

const PARTICIPANT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'canceled', label: 'Canceled' },
];

const MabarDetailCommunityPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [eventData, setEventData] = useState<any>(null);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<MabarParticipant | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventResponse, participantsResponse] = await Promise.all([
          communityService.getEventBySlug(slug!),
          communityService.getEventParticipants(slug!),
        ]);

        setEventData(eventResponse);
        setParticipants(participantsResponse.participants || []);
      } catch (error: any) {
        setNotification({
          isOpen: true,
          title: 'Error',
          message: error.message || 'Gagal memuat data event',
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  const handleStatusChange = async (participantId: number, newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      await communityService.updateParticipantStatus(
        slug!,
        participantId,
        newStatus as 'pending' | 'confirmed' | 'canceled'
      );

      setParticipants((prev) =>
        prev.map((p) =>
          p.id === participantId
            ? { ...p, status: newStatus as 'pending' | 'confirmed' | 'canceled' }
            : p
        )
      );

      setNotification({
        isOpen: true,
        title: 'Sukses',
        message: 'Status peserta berhasil diupdate',
        variant: 'success',
      });
    } catch (error: any) {
      setNotification({
        isOpen: true,
        title: 'Error',
        message: error.message || 'Gagal mengupdate status peserta',
        variant: 'error',
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteParticipant = async () => {
    if (!selectedParticipant) return;

    try {
      await communityService.deleteParticipant(slug!, selectedParticipant.id);

      setParticipants((prev) => prev.filter((p) => p.id !== selectedParticipant.id));

      setNotification({
        isOpen: true,
        title: 'Sukses',
        message: 'Peserta berhasil dihapus',
        variant: 'success',
      });

      setIsDeleteModalOpen(false);
      setSelectedParticipant(null);
    } catch (error: any) {
      setNotification({
        isOpen: true,
        title: 'Error',
        message: error.message || 'Gagal menghapus peserta',
        variant: 'error',
      });
    }
  };

  const handleShareLink = () => {
    const shareUrl = `${window.location.origin}/event/${slug}`;
    navigator.clipboard.writeText(shareUrl);
    showToast.success('Link event berhasil disalin ke clipboard');
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon icon="mdi:loading" className="w-12 h-12 animate-spin text-primary mx-auto mb-2" />
            <p className="text-gray-600">Memuat data event...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <Icon icon="mdi:alert-circle" className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Event tidak ditemukan</p>
          <Button className="mt-4" onClick={() => navigate('/host/mabar')}>
            Kembali ke Daftar Event
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/host/mabar')}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4"
        >
          <Icon icon="mdi:arrow-left" className="w-4 h-4" />
          Kembali ke Daftar Event
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{eventData.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {eventData.event_date} • {formatLocalEventTimeRange(eventData.start_time, eventData.end_time)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleShareLink}>
              <Icon icon="mdi:share-variant" className="w-4 h-4 mr-1" />
              Share Link
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/host/mabar/${slug}/edit`)}
            >
              <Icon icon="mdi:pencil" className="w-4 h-4 mr-1" />
              Edit Event
            </Button>
          </div>
        </div>
      </div>

      {/* Event Info Card */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Informasi Event</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Lokasi</p>
            <p className="font-medium">{eventData.lokasi_arena}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Kota</p>
            <p className="font-medium">{eventData.city}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Kategori Olahraga</p>
            <p className="font-medium">{eventData.sport_category_name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Tipe</p>
            <p className="font-medium">
              {eventData.visibility === 'public' ? 'Public' : 'Private'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Biaya</p>
            <p className="font-medium">{eventData.type === 'free' ? 'Gratis' : 'Berbayar'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Slot</p>
            <p className="font-medium">
              {eventData.filled_slots || 0} / {eventData.total_slots || 0} peserta
            </p>
          </div>
        </div>
      </div>

      {/* Participants Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Daftar Peserta ({participants.length})</h2>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setNotification({
                isOpen: true,
                title: 'Info',
                message: 'Fitur tambah peserta manual akan segera hadir!',
                variant: 'success',
              });
            }}
          >
            <Icon icon="mdi:plus" className="w-4 h-4 mr-1" />
            Tambah Peserta
          </Button>
        </div>

        {participants.length === 0 ? (
          <div className="text-center py-12">
            <Icon
              icon="mdi:account-group-outline"
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
            />
            <p className="text-gray-600">Belum ada peserta yang mendaftar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nama
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    WhatsApp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {participants.map((participant) => (
                  <tr key={participant.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{participant.name}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{participant.email || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{participant.wa_number || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{participant.role || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SimpleSelect
                        value={participant.status}
                        onValueChange={(status) => handleStatusChange(participant.id, status)}
                        options={PARTICIPANT_STATUS_OPTIONS}
                        disabled={isUpdatingStatus}
                        triggerClassName="h-8 px-2 text-sm"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedParticipant(participant);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Icon icon="mdi:delete" className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedParticipant(null);
        }}
        title="Konfirmasi Hapus Peserta"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin ingin menghapus peserta <strong>{selectedParticipant?.name}</strong>?
          </p>
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedParticipant(null);
              }}
            >
              Batal
            </Button>
            <Button type="button" variant="primary" onClick={handleDeleteParticipant}>
              Ya, Hapus
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default MabarDetailCommunityPage;
